<template>
    <app-layout>
        <template v-slot:header>
            <Header
                :title="$t('accounts_receivables_dashboard')"
                :is-filter="false"
            />
        </template>
        <template v-slot:header-mobile>
            <HeaderMobile
                :is-filter="false"
                :title="$t('accounts_receivables_dashboard')"
            />
        </template>

        <!-- Step Cards -->
        <div v-if="loading" class="d-flex justify-content-center align-items-center" style="height: 50vh;">
            <loading :show="loading"/>
        </div>
        <b-container v-else fluid class="px-3 py-4">
            <!-- Filter -->
            <datatable-filter @filter="onFilter" @filterClear="onFilterClear" class="mb-4">
                <b-row>
                    <b-col md="6" lg="3" class="mb-3 mb-lg-0">
                        <b-form-group :label="$t('academic_year')" class="mb-0 text-sm">
                            <academic-years-selectbox
                                v-model="filterData.academic_year"
                                :label="$t('academic_year')"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="6" lg="3">
                        <b-form-group :label="$t('semester')" class="mb-0 text-sm">
                            <semester-type-selectbox v-model="filterData.semester"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
            </datatable-filter>

            <b-row class="mx-n2">
                <b-col
                    v-for="stat in dashboardStats"
                    :key="stat.id"
                    cols="12" sm="6" md="4" lg="3" xl="2"
                    class="px-2 mb-3"
                >
                    <b-card
                        no-body
                        class="h-100 shadow-sm dashboard-card border-0 cursor-pointer"
                        :class="{ 'active-card': selectedStep === stat.id }"
                        @click="selectStep(stat.id)"
                    >
                        <b-card-body class="d-flex flex-column p-3">
                            <div class="d-flex justify-content-between align-items-start mb-3">
                                <span
                                    class="font-weight-600 text-truncate pr-2"
                                    :title="stat.title"
                                    style="font-size: 0.85rem; color: #4a5568;"
                                >
                                    {{ stat.title }}
                                </span>
                            </div>
                            <div class="text-center my-auto py-1">
                                <div :class="['stat-value']">
                                    {{ stat.value }}
                                </div>
                            </div>
                        </b-card-body>
                    </b-card>
                </b-col>
            </b-row>

            <!-- Sub Filter for Datatable -->
            <datatable-filter
                @filter="onSubFilter"
                @filterClear="onSubFilterClear"
                :exportExcel="checkPermission('accountsreceivable_export')"
                @exportExcel="onExportExcel"
                class="mb-3"
                :isTitle="false"
                :isSmallFilter="false"
            >
                <b-row class="mb-2">
                    <b-col md="4" lg="3" class="mb-2">
                        <b-form-group :label="$t('student_id')" class="mb-0 text-sm">
                            <b-form-input v-model="subFilterData.student_number" size="sm"></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col md="4" lg="3" class="mb-2">
                        <b-form-group :label="$t('name')" class="mb-0 text-sm">
                            <b-form-input v-model="subFilterData.name" size="sm"></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col md="4" lg="3" class="mb-2">
                        <b-form-group :label="$t('surname')" class="mb-0 text-sm">
                            <b-form-input v-model="subFilterData.surname" size="sm"></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col md="4" lg="3" class="mb-2">
                        <b-form-group :label="$t('program')" class="mb-0 text-sm">
                            <program-selectbox v-model="subFilterData.program_code" size="sm" :label="$t('program')"></program-selectbox>
                        </b-form-group>
                    </b-col>
                    <b-col md="4" lg="3" class="mb-2">
                        <b-form-group :label="$t('due_date')" class="mb-0 text-sm">
                             <select-date v-model="subFilterData.due_date" size="sm"></select-date>
                        </b-form-group>
                    </b-col>
                    <b-col md="4" lg="3" class="mb-2">
                        <b-form-group :label="$t('step')" class="mb-0 text-sm">
                            <b-form-select v-model="subFilterData.step" size="sm" :options="stepOptions"></b-form-select>
                        </b-form-group>
                    </b-col>
                    <b-col md="4" lg="3" class="mb-2">
                         <b-form-group :label="$t('status')" class="mb-0 text-sm">
                            <b-form-input v-model="subFilterData.status" size="sm"></b-form-input>
                        </b-form-group>
                    </b-col>
                </b-row>
            </datatable-filter>

            <!-- Datatable -->
            <datatable
                :isLoading.sync="datatable.isLoading"
                :columns="datatable.columns"
                :rows="datatable.rows"
                :total="datatable.total"
                :queryParams="datatable.queryParams"
                :lineNumbers="false"
                :selectOptions="isStep3Active"
                :selectedActionButtonText="showSendToCfoButton ? ($t('send_to_cfo') || 'Send to CFO') : ''"
                @selected-bulk-action="sendToCfo"
                @on-selected-rows-change="onSelectedRowsChange"
                @on-page-change="onPageChange"
                @on-sort-change="onSortChange"
                @on-per-page-change="onPerPageChange"
                v-show="datatable.showTable"
            />
        </b-container>

        <!-- Detail Modal -->
        <CommonModal ref="detailModal" size="xl" :onHideOnlyX="true" @bHideModalHeaderClose="closeDetail">
            <template v-slot:CommonModalTitle>
                {{ $t('detail').toUpperCase() }}
            </template>
            <template v-slot:CommonModalContent>
                <detail-modal
                    v-if="detailId"
                    :formId="detailId"
                    @updateSuccess="updateSuccess"
                />
            </template>
        </CommonModal>
    </app-layout>
</template>

<script>
import AccountsReceivablesDashboardServices from "@/services/AccountsReceivablesDashboardServices";
import Loading from "@/components/elements/Loading.vue";
import Datatable from "@/components/datatable/Datatable";
import CommonModal from "@/components/elements/CommonModal";
import DetailModal from "./DetailModal";
import DatatableFilter from "@/components/datatable/DatatableFilter";
import AcademicYearsSelectbox from "@/components/interactive-fields/AcademicYearsSelectbox";
import SemestersSelectbox from "@/components/interactive-fields/SemestersSelectbox";
import ProgramSelectbox from "@/components/interactive-fields/ProgramSelectbox";
import SelectDate from "@/components/interactive-fields/SelectDate";
import StudentDocumentService from "@/services/StudentDocumentService";
import {EventBus} from '@/main'


import qs from 'qs';
import SemesterTypeSelectbox from "@/components/interactive-fields/SemesterTypeSelectbox.vue";

export default {
    components: {
        SemesterTypeSelectbox,
        Loading,
        Datatable,
        CommonModal,
        DetailModal,
        DatatableFilter,
        AcademicYearsSelectbox,
        SemestersSelectbox,
        ProgramSelectbox,
        SelectDate,
    },
    data() {
        return {
            selectedRows: [],
            filterData: {
                academic_year: null,
                semester: null
            },
            subFilterData: {
                student_number: null,
                name: null,
                surname: null,
                program_code: null,
                due_date: null,
                step: null,
                status: null
            },
            dashboardStats: [],
            loading: false,
            selectedStep: null,
            detailId: null,
            selectedStudent:null,
            datatable: {
                isLoading: false,
                columns: [
                    {
                        label: '',
                        field: 'buttons',
                        html: true,
                        sortable: false,
                        tdClass: 'p-0 text-center w-40 align-middle',
                        buttons: [
                            {
                                text: this.$t('detail'),
                                class: 'ri-search-eye-line',
                                callback: (row) => {
                                    this.showDetail(row);
                                }
                            },
                            {
                                text: this.$t('view_and_edit'),
                                class: 'ri-search-eye-line',
                                callback: (row) => {
                                    window.open("/students/" + row.student_program_id, '_blank');
                                }
                            },
                             {
                                text: this.$t("payment_info"),
                                class: "ri-money-dollar-circle-line",
                                permission: "customer_customerpayments",
                                callback: (row) => {
                                    window.open("/customers/payments/student/" + row.student_program_id, '_blank');
                                },
                            },
                            {
                                text: this.$t("official_transcript"),
                                class: "ri-bar-chart-2-line",
                                permission: "studentdocument_officialtranscript",
                                callback: (row) => {
                                    StudentDocumentService.downloadOfficialTranscript(row.id,row.program_level)
                                        .then(response => {
                                            let fileName = row.student_number +' - '+ row.name+' '+row.surname + ' - Official Transcript.pdf'
                                            EventBus.$emit('pdfViewerModalShow', {
                                                pdfFileData: response.data,
                                                pdfFileName: fileName
                                            });
                                        })
                                        .catch(e => {
                                           this.showErrors(e, null, true)
                                        })
                                },
                            },
                        ]
                    },
                    {
                        label: this.$t('student_id'),
                        field: 'student_number',
                        sortable: true,
                    },
                    {
                        label: this.$t('name'),
                        field: 'name',
                        sortable: true,
                    },
                    {
                        label: this.$t('surname'),
                        field: 'surname',
                        sortable: true,
                    },
                    {
                        label: this.$t('program'),
                        field: 'program',
                        sortable: true,
                    },
                    {
                        label: this.$t('amount'),
                        field: 'amount',
                        sortable: true,
                        formatFn: (value) => {
                            return this.currencyFormat(value)
                        }
                    },
                    {
                        label: this.$t('due_date'),
                        field: 'due_date',
                        sortable: true,
                    },
                    {
                        label: this.$t('step'),
                        field: 'step_name',
                        sortable: true,
                    },
                    {
                        label: this.$t('status'),
                        field: 'status_name',
                        sortable: true,
                    },
                ],
                rows: [],
                total: 0,
                showTable: false,
                queryParams: {
                    filter: {},
                    sort: 'id',
                    page: 1,
                    limit: 20
                }
            },
        }
    },
    computed: {
        isStep3Active() {
            let activeStepObj = this.dashboardStats.find(s => s.id === this.selectedStep);
            if (!activeStepObj && this.subFilterData.step) {
                activeStepObj = this.dashboardStats.find(s => s.id === this.subFilterData.step);
            }
            if (!activeStepObj) return false;
            return activeStepObj.title === 'Step 3' || activeStepObj.title.toLowerCase().includes('step 3');
        },
        showSendToCfoButton() {
            return this.isStep3Active && this.selectedRows.length > 0 && this.checkPermission('accountsreceivable_sendtocfo');
        },
        stepOptions() {
            return [
                { value: null, text: this.$t('select') },
                ...this.dashboardStats
                    .filter(s => s.id !== 'total')
                    .map(s => ({ value: s.id, text: s.title }))
            ];
        }
    },
    created() {
        this.getDashboardStats();
    },
    methods: {
        async getDashboardStats() {
            this.loading = true;
            try {
                const config = {
                    params: {
                        filter: {
                            academic_year: this.filterData.academic_year,
                            semester: this.filterData.semester
                        }
                    },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };
                const res = await AccountsReceivablesDashboardServices.getDashboardStatsInformation(config)
                this.dashboardStats = res.data.data.map((item) => {
                    return {
                        id: item.step,
                        title: item.step_name,
                        value: item.count,
                    }
                });
            } catch (error) {
                this.showErrors(error, null);
            } finally {
                this.loading = false;
            }
        },
        onFilter() {
            this.datatable.queryParams.filter.academic_year = this.filterData.academic_year;
            this.datatable.queryParams.filter.semester = this.filterData.semester;
            this.datatable.queryParams.page = 1;

            this.getDashboardStats();

            if (this.datatable.showTable) {
                this.getRows();
            }
        },
        onFilterClear() {
            this.filterData = {
                academic_year: null,
                semester: null
            };
            this.datatable.queryParams.filter.academic_year = null;
            this.datatable.queryParams.filter.semester = null;
            this.datatable.queryParams.page = 1;

            this.getDashboardStats();

            if (this.datatable.showTable) {
                this.getRows();
            }
        },
        onSubFilter() {
            this.datatable.queryParams.filter = {
                ...this.datatable.queryParams.filter,
                ...this.subFilterData
            };
            this.datatable.queryParams.page = 1;
            this.getRows();
        },
        onSubFilterClear() {
            this.subFilterData = {
                student_number: null,
                name: null,
                surname: null,
                program_code: null,
                due_date: null,
                step: null,
                status: null
            };
            Object.keys(this.subFilterData).forEach(key => {
                this.datatable.queryParams.filter[key] = null;
            });
            this.datatable.queryParams.page = 1;
            this.getRows();
        },
        async onExportExcel() {
            try {
                this.loading = true;
                const config = {
                    params: {
                        ...this.datatable.queryParams,
                        limit: -1,
                    },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false }),

                };

                const res = await AccountsReceivablesDashboardServices.exportExcel(config);
                this._downloadFile(res, 'accounts_receivables.xlsx')
            } catch (error) {
                this.showErrors(error, null,true);
            } finally {
                this.loading = false;
            }
        },
        selectStep(stepId) {
            this.selectedStep = stepId;
            this.datatable.queryParams.filter = {
                step: stepId,
                academic_year: this.filterData.academic_year,
                semester: this.filterData.semester
            };
            this.subFilterData.step = stepId;
            this.datatable.queryParams.page = 1;
            if (stepId === 'total') {
                this.datatable.queryParams.limit = -1;
                this.datatable.queryParams.filter = {
                    academic_year: this.filterData.academic_year,
                    semester: this.filterData.semester
                };
            }
            this.getRows();
        },
        getRows() {
            this.datatable.showTable = true;
            this.datatable.isLoading = true;

            const config = {
                params: {
                    ...this.datatable.queryParams
                },
                paramsSerializer: (params) => qs.stringify(params, { encode: false })
            };
            AccountsReceivablesDashboardServices.getAll(config)
                .then((response) => {
                    this.datatable.rows = response.data.data;
                    this.datatable.total = response.data.pagination.total;
                })
                .finally(() => {
                    this.datatable.isLoading = false;
                });
        },
        onPageChange(params) {
            this.datatable.queryParams.page = params.currentPage;
            this.getRows();
        },
        onPerPageChange(params) {
            this.datatable.queryParams.limit = params.currentPerPage;
            this.datatable.queryParams.page = 1;
            this.getRows();
        },
        onSortChange(params) {
            const sortType = params[0].type == 'desc' ? '-' : '';
            this.datatable.queryParams.sort = sortType + params[0].field;
            this.getRows();
        },
        showDetail(row) {
            this.detailId = row.id;
            this.selectedStudent=row
            this.$refs.detailModal.$refs.commonModal.show();
        },
        closeDetail() {
            this.detailId = null;
        },
        updateSuccess() {
            this.getRows()
            this.getDashboardStats()
            this.$refs.detailModal.$refs.commonModal.hide();

        },
        onSelectedRowsChange(params) {
            this.selectedRows = params.selectedRows;
        },
        async sendToCfo() {
            if (!this.selectedRows || this.selectedRows.length === 0) return;
            this.$swal
                .fire({
                    text: this.$t('are_you_sure_to_send'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no'),
                })
                .then((result) => {
                    if (result.isConfirmed) {
                        try {
                        this.loading = true;
                        const payload = {
                            filter: {
                                id: this.selectedRows.map(row => row.id)
                            }
                        };
                        AccountsReceivablesDashboardServices.sendToCfo(payload);
                        this.toast({ success: true, message: 'successfully_updated' });
                        this.selectedRows = [];
                        this.getDashboardStats();
                        this.getRows();
                    } catch (error) {
                        this.showErrors(error, null, true);
                    } finally {
                            this.loading = false;
                        }
                    }
                })


            this.confirmModal(this.$t('are_you_sure_to_send'), async () => {
                try {
                    this.loading = true;
                    const payload = {
                        filter: {
                            id: this.selectedRows.map(row => row.id)
                        }
                    };
                    const res =await AccountsReceivablesDashboardServices.sendToCfo(payload);
                    this.$toast.success(this.$t("api." + res.data.message));
                    this.selectedRows = [];
                    this.getDashboardStats();
                    this.getRows();
                } catch (error) {
                    this.showErrors(error, null, true);
                } finally {
                    this.loading = false;
                }
            });
        },
    }
}
</script>

<style scoped>
.dashboard-card {
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
    border-radius: 6px;
    border: 1px solid #edf2f7 !important;
}
.dashboard-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
}
.active-card {
    border: 2px solid #4299e1 !important;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.3) !important;
}

.stat-value {
    font-size: 2.75rem;
    font-weight: 400;
    line-height: 1;
    letter-spacing: -1px;
}

.border-box {
    display: inline-block;
    border: 2px solid #000000;
    padding: 0px 8px;
    border-radius: 2px;
}

.text-dark-green   { color: #1b6555; }
.text-warning-custom { color: #d69e2e; }
.text-danger-custom  { color: #c53030; }

.font-weight-600 { font-weight: 600; }
.cursor-pointer  { cursor: pointer; }
.line-height-1   { line-height: 1.2; }
</style>
